import React from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Select } from 'antd';
import type { Moment } from 'moment';

import Title from '@src/components/BoxContainer/components/Title';
import type {
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import { selectCalendarTimeZone } from '@src/packages/utils/dates/calendar';
import RelativeAbsolutePeriodFilter, {
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import IntlMessages from '@src/util/IntlMessages';

import { dateTypes } from '../../constants';
import {
  buildScheduleFilterSetValueProps,
  DEFAULT_DATE_RANGE,
  filterValuesToScheduleFiltersUi,
  periodValueToScheduleFilterProps,
  scheduleFiltersToPeriodValue,
  SCHEDULES_REPORT_MAX_SPAN,
  SCHEDULES_DASHBOARD_FILTER_FIELDS,
  type ScheduleChartFilters,
} from '../../schedulesFilterConfig';
import type { ScheduleDashboardDateType } from '../../types';
import ScheduleSelector from '../ScheduleSelector';

const { Option } = Select;

const DEFAULT_ABSOLUTE_RANGE = (): [Moment, Moment] => [...DEFAULT_DATE_RANGE];

interface ScheduleDashboardFiltersProps {
  handleFilters: (props: SetValueProps | SetValueProps[]) => void;
  filters: FilterValues;
  chartFilters: ScheduleChartFilters;
  divisionIds: string[];
  disabledDate: (date: Moment) => boolean;
  onSchedulesChange: (schedules: ScheduleChartFilters['schedules']) => void;
  onScheduleSelectorLoading: (loading: boolean) => void;
}

const ScheduleDashboardFilters: React.FC<ScheduleDashboardFiltersProps> = ({
  handleFilters,
  filters,
  chartFilters,
  divisionIds,
  onSchedulesChange,
  onScheduleSelectorLoading,
}) => {
  const intl = useIntl();
  const organizationTimeZone = useSelector(selectCalendarTimeZone);
  const uiFilters = filterValuesToScheduleFiltersUi(filters, chartFilters, organizationTimeZone);
  const { groupBy, schedules } = uiFilters;
  const periodValue = scheduleFiltersToPeriodValue(uiFilters);

  const setFilterValue = (key: string, value: unknown) => {
    handleFilters(buildScheduleFilterSetValueProps(key, value, intl));
  };

  const handlePeriodChange = (next: RelativeAbsolutePeriodValue) => {
    handleFilters(periodValueToScheduleFilterProps(next, intl));
  };

  const handleGroupByChange = (value: ScheduleDashboardDateType) => {
    setFilterValue(SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY, value);
  };

  return (
    <div className="gx-flex-column gx-guarnic-gap-3">
      <RelativeAbsolutePeriodFilter
        historicalOnly
        value={periodValue}
        onChange={handlePeriodChange}
        label={
          <span className="gx-guarnic-title-filter">
            <IntlMessages id="dashboards.schedules.dateRange" />
          </span>
        }
        allowClear={false}
        defaultAbsoluteRange={DEFAULT_ABSOLUTE_RANGE}
        maxSpan={SCHEDULES_REPORT_MAX_SPAN}
        showPresets
        timeZone={organizationTimeZone}
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="dashboards.schedules.groupResultsBy" />} />}
        value={
          <Select<ScheduleDashboardDateType>
            id="groupBy"
            onChange={handleGroupByChange}
            value={groupBy}
            dropdownMatchSelectWidth={false}
          >
            {dateTypes.map(d => (
              <Option key={d} value={d}>
                <IntlMessages id={`dateTypes.${d}`} values={{ amount: 1 }} />
              </Option>
            ))}
          </Select>
        }
      />
      <KeyValueWidget
        label={<Title.Filter value={<IntlMessages id="schedules.type.schedule" />} />}
        value={
          <ScheduleSelector
            selectedSchedules={schedules}
            setSelected={onSchedulesChange}
            setLoading={onScheduleSelectorLoading}
            divisionIds={divisionIds}
          />
        }
      />
    </div>
  );
};

export default ScheduleDashboardFilters;
